import React from 'react';
import {
    Box, Typography, List, ListItem, Chip, Paper, Button,
    IconButton, Select, MenuItem, FormControl, CircularProgress,
    Skeleton, TextField, InputAdornment, Autocomplete
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import throttle from 'lodash/throttle';

import { StyledDialog, StyledDialogTitle } from '../../main_components/material_table/styles/general_form_styles';
import COMMENTS_SUPPORT from './tools/custom/comments';
import type { SUPPORTS } from "../interface/interface";

const severityPriority: Record<string, number> = {
    'Alta': 1, 'Media': 2, 'Baja': 3
};

const severityColors: Record<string, { bg: string; color: string }> = {
    'Alta': { bg: '#FEE2E2', color: '#B91C1C' },
    'Media': { bg: '#FEF9C3', color: '#92400E' },
    'Baja': { bg: '#D1FAE5', color: '#065F46' }
};

const statusColors: Record<string, { bg: string; color: string; label: string }> = {
    'OPEN': { bg: '#E0E7FF', color: '#3730A3', label: 'ABIERTO' },
    'CLOSE': { bg: '#E5E7EB', color: '#6B7280', label: 'FINALIZADO' },
    'PROCESS': { bg: '#e8e4f2', color: '#1E40AF', label: 'EN PROCESO' },
    'REVIEW': { bg: '#FDE68A', color: '#92400E', label: 'EN REVISIÓN' },
    'VALIDATION': { bg: '#FEF9C3', color: '#92400E', label: 'EN VALIDACIÓN' },
};

interface TicketListProps {
    tickets: SUPPORTS[];
    onViewAll?: () => void;
    fetchSupports: () => Promise<SUPPORTS[]>;
    flagModal: boolean;
    onUpdateTicket: (updated: SUPPORTS) => void;
}

const TicketList: React.FC<TicketListProps> = ({ tickets, onViewAll, flagModal, onUpdateTicket }) => {
    const [open_form, setOpenForm] = React.useState(false);
    const [selectedTicket, setSelectedTicket] = React.useState<null | SUPPORTS>(null);
    const [selectedStatus, setSelectedStatus] = React.useState('OPEN');
    const [selectedCompany, setSelectedCompany] = React.useState<string>('ALL');
    const [selectedUser, setSelectedUser] = React.useState<string | null>(null);
    const [isFiltering, setIsFiltering] = React.useState(false);
    const [statusFilteredTickets, setStatusFilteredTickets] = React.useState<SUPPORTS[]>([]);
    const [filteredTickets, setFilteredTickets] = React.useState<SUPPORTS[]>([]);
    const [throttledSearch, setThrottledSearch] = React.useState('');
    const [inputValue, setInputValue] = React.useState('');
    const [visibleCount, setVisibleCount] = React.useState(20);

    const listRef = React.useRef<HTMLDivElement | null>(null);

    // Obtener opciones únicas para los filtros
    const companyOptions = React.useMemo(() => {
        const companies = new Set<string>(['ALL']); // 'ALL' como opción por defecto
        tickets.forEach(ticket => {
            if (ticket.company_code) {
                companies.add(ticket.company_code);
            }
        });
        return Array.from(companies);
    }, [tickets]);

    const userOptions = React.useMemo(() => {
        const users = new Set<string>();
        tickets.forEach(ticket => {
            if (ticket.user_name) {
                users.add(ticket.user_name);
            }
        });
        return Array.from(users).sort();
    }, [tickets]);

    const throttledUpdate = React.useMemo(() => throttle((val: string) => {
        setThrottledSearch(val);
    }, 300), []);

    const handleClose = () => setOpenForm(false);

    const handleStatusChange = (newStatus: string) => {
        setIsFiltering(true);
        setTimeout(() => {
            setSelectedStatus(newStatus);
            setIsFiltering(false);
        }, 700);
    };

    const handleCompanyChange = (newCompany: string) => {
        setIsFiltering(true);
        setTimeout(() => {
            setSelectedCompany(newCompany);
            setIsFiltering(false);
        }, 700);
    };

    const handleUserChange = (newUser: string | null) => {
        setIsFiltering(true);
        setTimeout(() => {
            setSelectedUser(newUser);
            setIsFiltering(false);
        }, 700);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        throttledUpdate(val);
    };

    const clearSearch = () => {
        setInputValue('');
        throttledUpdate('');
    };

    const clearAllFilters = () => {
        setSelectedStatus('OPEN');
        setSelectedCompany('ALL');
        setSelectedUser(null);
        setInputValue('');
        throttledUpdate('');
    };

    React.useEffect(() => {
        let filtered = tickets;
        
        // Filtro por estado
        if (selectedStatus !== 'ALL') {
            filtered = filtered.filter(ticket => ticket.status === selectedStatus);
        }
        
        // Filtro por compañía
        if (selectedCompany !== 'ALL') {
            filtered = filtered.filter(ticket => ticket.company_code === selectedCompany);
        }
        
        // Filtro por usuario
        if (selectedUser) {
            filtered = filtered.filter(ticket => ticket.user_name === selectedUser);
        }
        
        setStatusFilteredTickets(filtered);
    }, [selectedStatus, selectedCompany, selectedUser, tickets]);

    React.useEffect(() => {
        const filtered = statusFilteredTickets.filter(ticket =>
            ticket.folio?.toLowerCase().includes(throttledSearch.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(throttledSearch.toLowerCase())
        );

        const timeout = setTimeout(() => {
            const sorted = filtered.sort((a, b) => {
                if (a.recurrent_incidence && !b.recurrent_incidence) return -1;
                if (!a.recurrent_incidence && b.recurrent_incidence) return 1;
                const aPriority = severityPriority[a.severity || ''] ?? 4;
                const bPriority = severityPriority[b.severity || ''] ?? 4;
                if (aPriority !== bPriority) return aPriority - bPriority;
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            });
            setFilteredTickets(sorted);
            setVisibleCount(20); // reinicia cantidad visible
        }, 0);

        return () => clearTimeout(timeout);
    }, [throttledSearch, statusFilteredTickets]);

    const handleScroll = () => {
        if (!listRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10) {
            setVisibleCount(prev => prev + 20);
        }
    };

    React.useEffect(() => {
        const currentRef = listRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const ticketsToShow = filteredTickets.slice(0, visibleCount);

    return (
        <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
            <StyledDialog open={open_form} maxWidth="lg" fullWidth>
                <StyledDialogTitle>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                        <Chip label={selectedTicket?.folio} size="small" sx={{ mr: 1, backgroundColor: '#E0E7FF', color: '#3730A3' }} />
                        <span style={{ color: 'white' }}>{selectedTicket?.subject}</span>
                    </Typography>
                    <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </StyledDialogTitle>
                {selectedTicket && <COMMENTS_SUPPORT tickets={selectedTicket} onTicketUpdate={onUpdateTicket} />}
            </StyledDialog>

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Tickets</Typography>
                    <FormControl size="small" sx={{
                        minWidth: 140,
                        backgroundColor: isFiltering ? '#DBEAFE' : statusColors[selectedStatus]?.bg || '#F3F4F6',
                        borderRadius: 3, px: 1, height: 30
                    }}>
                        <Select
                            variant="standard"
                            disableUnderline
                            value={selectedStatus}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            displayEmpty
                            renderValue={() =>
                                isFiltering ? (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CircularProgress size={14} color="secondary" />
                                        <Typography fontSize={13} fontWeight={700} color="#7C3AED">Filtrando...</Typography>
                                    </Box>
                                ) : (
                                    <Typography fontSize={14} fontWeight={600} color={statusColors[selectedStatus]?.color || '#374151'}>
                                        {statusColors[selectedStatus]?.label || 'TODOS'}
                                    </Typography>
                                )
                            }
                            sx={{
                                fontWeight: 600, fontSize: '14px', px: 1,
                                height: 30, display: 'flex', alignItems: 'center',
                                color: statusColors[selectedStatus]?.color || '#374151'
                            }}
                        >
                            {Object.entries(statusColors).map(([key, { label }]) => (
                                <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                            <MenuItem value="ALL">TODOS</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                    <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={clearAllFilters}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Limpiar filtros
                    </Button>
                    <Button 
                        variant="contained" 
                        size="small" 
                        startIcon={<VisibilityIcon />}
                        sx={{ backgroundColor: '#0472e9', textTransform: 'none', borderRadius: 2 }}
                        onClick={onViewAll}
                    >
                        Ver todo
                    </Button>
                </Box>
            </Box>

            {/* Filtros */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                {/* Buscador */}
                <TextField
                    fullWidth
                    placeholder="Buscar por folio o asunto"
                    size="small"
                    value={inputValue}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                            </InputAdornment>
                        ),
                        endAdornment: inputValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={clearSearch} edge="end" sx={{ color: '#9CA3AF' }}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            height: 36, fontSize: 14, borderRadius: '12px',
                            backgroundColor: '#F3F4F6', color: '#111827',
                        }
                    }}
                    sx={{
                        flex: 2,
                        '& .MuiOutlinedInput-root': { borderRadius: '12px', paddingRight: 1 },
                        '& input::placeholder': { fontSize: '14px', color: '#9CA3AF' },
                    }}
                />

                {/* Filtro de Compañía */}
                {/* <FormControl size="small" sx={{ flex: 1 }}>
                    <Select
                        value={selectedCompany}
                        onChange={(e) => handleCompanyChange(e.target.value as string)}
                        displayEmpty
                        sx={{
                            height: 36,
                            borderRadius: '12px',
                            backgroundColor: '#F3F4F6',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }}
                    >
                        <MenuItem value="ALL">Todas las compañías</MenuItem>
                        {companyOptions
                            .filter(company => company !== 'ALL')
                            .map((company) => (
                                <MenuItem key={company} value={company}>
                                    {company}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl> */}

                {/* Filtro de Usuario (Autocomplete) */}
                <Autocomplete
                    options={userOptions}
                    value={selectedUser}
                    onChange={(_, newValue) => handleUserChange(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Filtrar por usuario"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    height: 36,
                                    borderRadius: '12px',
                                    backgroundColor: '#F3F4F6',
                                }
                            }}
                        />
                    )}
                    sx={{ flex: 1 }}
                    disableClearable={false}
                />
            </Box>

            {/* Lista de tickets */}
            {flagModal ? (
                <List>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <ListItem key={i} divider>
                            <Skeleton variant="rectangular" height={40} width="100%" />
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box ref={listRef} sx={{ height: 300, overflowY: 'auto' }}>
                    <List>
                        {ticketsToShow.map((ticket) => {
                            const severity = ticket?.severity || 'Desconocida';
                            const severityStyle = severityColors[severity] || { bg: '#E5E7EB', color: '#374151' };
                            const statusStyle = statusColors[ticket?.status ?? 'UNKNOWN'] || {
                                bg: '#E5E7EB', color: '#374151', label: 'Estado desconocido'
                            };

                            return (
                                <ListItem key={ticket?.ticket} divider
                                    sx={{
                                        py: 2, px: 2,
                                        backgroundColor: ticket?.recurrent_incidence ? '#FFF7ED' : 'transparent',
                                        borderLeft: ticket?.recurrent_incidence ? '4px solid #FB923C' : 'none',
                                        transition: 'background-color 0.3s ease'
                                    }}
                                >
                                    <Box display="flex" flexDirection="column" width="100%">
                                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                                            <Box sx={{ textAlign: "justify", textTransform: "uppercase" }}>
                                                <Typography variant="subtitle2">
                                                    {`${ticket?.folio || ticket?.ticket.slice(0, 8)} - ${ticket?.subject}`}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip label={severity} sx={{
                                                    backgroundColor: severityStyle.bg,
                                                    color: severityStyle.color, borderRadius: 2, width: 80
                                                }} />
                                                <Chip label={statusStyle.label} sx={{
                                                    backgroundColor: statusStyle.bg,
                                                    color: statusStyle.color, borderRadius: 2, width: 120
                                                }} />
                                                <IconButton size="small" onClick={() => {
                                                    setSelectedTicket(ticket);
                                                    setOpenForm(true);
                                                }}>
                                                    <ChatBubbleOutlineIcon fontSize="small" sx={{ color: '#7C3AED' }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} sx={{ textTransform: "uppercase" }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Creado por: {ticket.user_name || 'Desconocido'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Creado: {new Date(ticket.date).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>
                    {visibleCount < filteredTickets.length && (
                        <Box textAlign="center" p={1}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default TicketList;